<template>
  <div class="accueil-dashboard">
    <greeting-widget
      class="mb-3"
      :loading="isRefreshing"
      @refresh="refreshData"
    />
    <quick-access-bar class="mb-3" />

    <financial-summary-widget
      v-if="canShow.financial"
      ref="financialSummary"
      class="mb-3"
    />

    <div v-if="homeLoading" class="accueil-dashboard-loading">
      <a-spin size="large" />
    </div>
    <template v-else-if="homeLoaded">
      <critical-tasks-panel
        class="mb-3"
        :tasks="dashboard.criticalTasks"
      />

      <counts-widget class="mb-3" :counts="dashboard.counts" />

      <requests-alerts-widget
        class="mb-3"
        :requests="dashboard.requests"
      />

      <div
        v-if="canShow.eleves || canShow.bulletin"
        class="row accueil-pair-row mb-3"
      >
        <div
          v-if="canShow.eleves"
          :class="canShow.bulletin ? 'col-xl-6 col-lg-12' : 'col-12'"
        >
          <birthdays-widget :birthdays="dashboard.birthdays" />
        </div>
        <div
          v-if="canShow.bulletin"
          :class="canShow.eleves ? 'col-xl-6 col-lg-12' : 'col-12'"
        >
          <top-students-widget ref="topStudents" />
        </div>
      </div>

      <div
        v-if="canShow.calendar || canShow.actualite"
        class="row accueil-pair-row mb-3"
      >
        <div
          v-if="canShow.calendar"
          :class="canShow.actualite ? 'col-xl-6 col-lg-12' : 'col-12'"
        >
          <upcoming-events-widget :events="dashboard.events" />
        </div>
        <div
          v-if="canShow.actualite"
          :class="canShow.calendar ? 'col-xl-6 col-lg-12' : 'col-12'"
        >
          <latest-news-widget :news="dashboard.actualities" />
        </div>
      </div>

      <div
        v-if="canShow.emplois || canShow.punition"
        class="row accueil-pair-row mb-3"
      >
        <div
          v-if="canShow.emplois"
          :class="canShow.punition ? 'col-xl-8 col-lg-12' : 'col-12'"
        >
          <room-availability-widget :rooms="dashboard.roomsAvailability" />
        </div>
        <div
          v-if="canShow.punition"
          :class="canShow.emplois ? 'col-xl-4 col-lg-12' : 'col-12'"
        >
          <discipline-breakdown-chart
            :breakdown="dashboard.disciplineBreakdown"
          />
        </div>
      </div>
    </template>

    <detailed-stats-widget ref="detailedStats" class="mb-3" />
  </div>
</template>

<script>
/* eslint-disable */
import GreetingWidget from "./components/GreetingWidget";
import QuickAccessBar from "./components/QuickAccessBar";
import CountsWidget from "./components/CountsWidget";
import FinancialSummaryWidget from "./components/FinancialSummaryWidget";
import CriticalTasksPanel from "./components/CriticalTasksPanel";
import RequestsAlertsWidget from "./components/RequestsAlertsWidget";
import BirthdaysWidget from "./components/BirthdaysWidget";
import TopStudentsWidget from "./components/TopStudentsWidget";
import UpcomingEventsWidget from "./components/UpcomingEventsWidget";
import LatestNewsWidget from "./components/LatestNewsWidget";
import RoomAvailabilityWidget from "./components/RoomAvailabilityWidget";
import DisciplineBreakdownChart from "./components/DisciplineBreakdownChart";
import DetailedStatsWidget from "./components/DetailedStatsWidget";
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import {
  canShowFeature,
  DASHBOARD_FEATURES,
} from "./utils/dashboardAccess";

export default {
  name: "AccueilDashboard",
  components: {
    GreetingWidget,
    QuickAccessBar,
    CountsWidget,
    FinancialSummaryWidget,
    CriticalTasksPanel,
    RequestsAlertsWidget,
    BirthdaysWidget,
    TopStudentsWidget,
    UpcomingEventsWidget,
    LatestNewsWidget,
    RoomAvailabilityWidget,
    DisciplineBreakdownChart,
    DetailedStatsWidget,
  },

  data() {
    return {
      homeLoading: false,
      homeLoaded: false,
      isRefreshing: false,
      dashboard: {
        counts: {},
        actualities: [],
        events: [],
        birthdays: [],
        requests: { parents: 0, eleves: 0, personnel: 0 },
        criticalTasks: {
          punitionsPendingValidation: 0,
          notificationsUnread: 0,
          messagesParentsUnanswered: 0,
          actualitesPendingValidation: 0,
          actualites: { totalCount: 0, publishedToday: false },
        },
        disciplineBreakdown: [],
        roomsAvailability: [],
      },
    };
  },

  computed: {
    ...mapState(["user", "settings"]),
    canShow() {
      const u = this.user;
      const s = this.settings;
      const features = DASHBOARD_FEATURES || {};
      return {
        financial: canShowFeature(u, s, features.financial),
        eleves: canShowFeature(u, s, features.eleves),
        bulletin: canShowFeature(u, s, features.bulletin),
        calendar: canShowFeature(u, s, features.calendar),
        actualite: canShowFeature(u, s, features.actualite),
        emplois: canShowFeature(u, s, features.emplois),
        punition: canShowFeature(u, s, features.punition),
      };
    },
  },

  created() {
    this.loadHome();
  },

  methods: {
    emptyDashboard() {
      return {
        counts: {},
        actualities: [],
        events: [],
        birthdays: [],
        requests: { parents: 0, eleves: 0, personnel: 0 },
        criticalTasks: {
          punitionsPendingValidation: 0,
          notificationsUnread: 0,
          messagesParentsUnanswered: 0,
          actualitesPendingValidation: 0,
          actualites: { totalCount: 0, publishedToday: false },
        },
        disciplineBreakdown: [],
        roomsAvailability: [],
      };
    },

    async loadHome() {
      this.homeLoading = true;
      try {
        const { data } = await apiClient.get("/home");
        const empty = this.emptyDashboard();
        this.dashboard = {
          counts: (data && data.counts) || empty.counts,
          actualities: (data && data.actualities) || empty.actualities,
          events: (data && data.events) || empty.events,
          birthdays: (data && data.birthdays) || empty.birthdays,
          requests: (data && data.requests) || empty.requests,
          criticalTasks: (data && data.criticalTasks) || empty.criticalTasks,
          disciplineBreakdown:
            (data && data.disciplineBreakdown) || empty.disciplineBreakdown,
          roomsAvailability:
            (data && data.roomsAvailability) || empty.roomsAvailability,
        };
        this.homeLoaded = true;
      } catch (error) {
        this.dashboard = this.emptyDashboard();
        this.homeLoaded = true;
      } finally {
        this.homeLoading = false;
      }
    },

    async refreshData() {
      this.isRefreshing = true;
      try {
        await Promise.all([
          this.loadHome(),
          this.$refs.financialSummary &&
          this.$refs.financialSummary.loadFinancialSummary
            ? this.$refs.financialSummary.loadFinancialSummary()
            : Promise.resolve(),
          this.$refs.detailedStats && this.$refs.detailedStats.loadStats
            ? this.$refs.detailedStats.loadStats()
            : Promise.resolve(),
          this.$refs.topStudents && this.$refs.topStudents.loadTopStudents
            ? this.$refs.topStudents.loadTopStudents()
            : Promise.resolve(),
        ]);
      } finally {
        this.isRefreshing = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./styles/dashboard.scss";

.accueil-dashboard-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24vh;
  padding: 32px 0;
}

.accueil-pair-row {
  margin-left: -6px;
  margin-right: -6px;

  > [class*="col-"] {
    padding-left: 6px;
    padding-right: 6px;
    margin-bottom: 12px;
  }

  @media (min-width: 1200px) {
    > [class*="col-"] {
      margin-bottom: 0;
    }
  }
}
</style>
